// View-model derivation for the open-positions card view. Mirrors the Open
// Positions table in PositionTracker so the card and table agree on every
// figure (avg / cost / mark / P&L / cash-out / if-win).

import type { PolyRelayPosition } from '../../lib/polymarket/relay';
import type { PositionWithPnl } from '../../hooks/ui/usePositionPnl';
import { truncateTicker } from '../../lib/kalshiTicker/describeMarket';
import { americanOdds } from '../../views/ValueDashboard/tradeModalBook';
import { titleParts } from './cardFormat';

export interface PositionCardVM {
  key: string;
  /** Market traded volume from the live frame (null pre-frame /
   *  non-streamed markets). Decorated by the view, not the builders. */
  volume?: number | null;
  venue: 'kalshi' | 'polymarket';
  ticker: string;
  title: string;
  subtitle: string;
  eventStartLabel: string | null;
  side: 'yes' | 'no';
  team: string | null;
  qty: number;
  /** Contracts of this position sitting on open sell orders. */
  pendingSellQty: number;
  avgCents: number;
  avgOdds: string | null;
  feesCents: number | null;
  /** Entry cost + fees, in dollars. */
  costDollars: number;
  /** Current side-native mark. Null when Hydra hasn't priced it. */
  nowCents: number | null;
  nowOdds: string | null;
  pnlDollars: number | null;
  cashOutDollars: number | null;
  ifWinDollars: number | null;
  /** Gross settlement payout on a win (qty × $1). */
  grossPayoutDollars: number | null;
  impliedOdds: string | null;
  /** Kalshi positions can be closed/edited; poly can't (yet). */
  editable: boolean;
}

export function buildKalshiPositionVM(
  pos: PositionWithPnl,
  pendingSellQty: number,
  eventStartLabel: string | null = null,
): PositionCardVM {
  const { title, subtitle, team } = titleParts(pos.ticker, pos.side);
  return {
    key: `${pos.ticker}-${pos.side}`,
    venue: 'kalshi',
    ticker: pos.ticker,
    title,
    subtitle,
    eventStartLabel,
    side: pos.side,
    team,
    qty: pos.position,
    pendingSellQty,
    avgCents: pos.averagePrice,
    avgOdds: americanOdds(pos.averagePrice),
    feesCents: pos.feesPaidCents ?? null,
    costDollars: pos.totalCostCents / 100,
    nowCents: pos.currentPrice,
    nowOdds: pos.currentPrice != null ? americanOdds(Math.round(pos.currentPrice)) : null,
    pnlDollars: pos.unrealizedPnl != null ? pos.unrealizedPnl / 100 : null,
    cashOutDollars: pos.cashOutCents != null ? pos.cashOutCents / 100 : null,
    ifWinDollars: pos.ifWinCents / 100,
    grossPayoutDollars: pos.position,
    impliedOdds:
      pos.position > 0 ? americanOdds(pos.totalCostCents / pos.position) : null,
    editable: true,
  };
}

export function buildPolyPositionVM(
  pos: PolyRelayPosition,
  eventStartLabel: string | null = null,
): PositionCardVM {
  const { title, subtitle, team } = titleParts(pos.ticker, pos.side);
  return {
    key: `poly-pos-${pos.ticker}-${pos.side}`,
    venue: 'polymarket',
    ticker: pos.ticker,
    title: title || truncateTicker(pos.ticker),
    subtitle,
    eventStartLabel,
    side: pos.side,
    team,
    qty: pos.netCount,
    pendingSellQty: 0,
    avgCents: pos.avgEntryPriceCents,
    avgOdds: americanOdds(pos.avgEntryPriceCents),
    feesCents: pos.feesPaidCents ?? null,
    costDollars: (pos.avgEntryPriceCents * pos.netCount) / 100,
    nowCents: null,
    nowOdds: null,
    pnlDollars: null,
    cashOutDollars: null,
    ifWinDollars: null,
    grossPayoutDollars: null,
    impliedOdds: null,
    editable: false,
  };
}

/** Mark-price tint — mirrors PositionTracker's nowColorClass buckets so the
 *  card's "now" figure reads the same red→green as the table's column. */
export function nowTone(cents: number | null | undefined): string {
  if (cents == null) return 'text-muted-foreground';
  if (cents <= 15) return 'text-red-500';
  if (cents <= 25) return 'text-red-400';
  if (cents <= 35) return 'text-red-300';
  if (cents <= 45) return 'text-orange-400';
  if (cents <= 55) return 'text-yellow-400';
  if (cents <= 65) return 'text-[#9aa00d]';
  if (cents <= 75) return 'text-green-300';
  if (cents <= 85) return 'text-green-400';
  return 'text-green-500';
}
